import type { FlattenedSession } from '../hooks/useAdminData';
import type { PlayedGameEntry } from '../types';
import { formatDuration } from './formatTime';
import { getDayBoundaries } from './dateUtils';

export interface GameStat {
  game: string;
  totalDurationMs: number;
  sessionCount: number;
  percentage: number;
}

export interface RoomStat {
  roomId: string;
  roomName: string;
  totalDurationMs: number;
  sessionCount: number;
  percentage: number;
  topGame?: string;
}

export interface EventReportSummary {
  periodLabel: string;
  totalSessions: number;
  totalDurationMs: number;
  averageSessionDurationMs: number;
  uniqueRoomsCount: number;
  uniqueStaffCount: number;
  stopwatchCount: number;
  countdownCount: number;
  topGames: GameStat[];
  roomStats: RoomStat[];
}

export type ReportPeriod = 'all' | 'today' | 'custom_day';

/**
 * Filtre les sessions selon la période choisie.
 */
export function filterSessionsByPeriod(
  sessions: FlattenedSession[],
  period: ReportPeriod,
  selectedDay?: string
): FlattenedSession[] {
  if (period === 'all') {
    return sessions;
  }

  const targetDay = selectedDay || new Date().toISOString().slice(0, 10);
  const { startOfDay, endOfDay } = getDayBoundaries(targetDay);

  return sessions.filter((s) => {
    const time = s.startedAt || s.endedAt;
    if (!time) return false;
    return time >= startOfDay && time <= endOfDay;
  });
}

/**
 * Calcule l'ensemble des métriques de synthèse pour un ensemble de sessions.
 */
export function computeEventReport(
  sessions: FlattenedSession[],
  periodLabel: string = 'Global'
): EventReportSummary {
  const totalSessions = sessions.length;
  let totalDurationMs = 0;
  let stopwatchCount = 0;
  let countdownCount = 0;

  const staffSet = new Set<string>();
  const roomMap = new Map<
    string,
    { name: string; durationMs: number; sessions: number; games: Map<string, number> }
  >();
  const gameMap = new Map<string, { durationMs: number; sessions: number }>();

  sessions.forEach((s) => {
    const duration = s.totalDurationMs || 0;
    totalDurationMs += duration;

    if (s.type === 'countdown') {
      countdownCount++;
    } else {
      stopwatchCount++;
    }

    if (s.stoppedBy) {
      staffSet.add(s.stoppedBy);
    }

    // Room stats
    const rId = s.roomId || 'unknown';
    const rName = s.roomName || 'Salle sans nom';
    if (!roomMap.has(rId)) {
      roomMap.set(rId, { name: rName, durationMs: 0, sessions: 0, games: new Map() });
    }
    const rEntry = roomMap.get(rId)!;
    rEntry.durationMs += duration;
    rEntry.sessions += 1;

    // Games breakdown
    const rawGames = s.playedGames;
    const gamesList: PlayedGameEntry[] = Array.isArray(rawGames)
      ? rawGames
      : rawGames
      ? Object.values(rawGames as Record<string, PlayedGameEntry>)
      : [];

    if (gamesList.length > 0) {
      gamesList.forEach((g) => {
        const gameName = (g.game || 'Jeu non spécifié').trim();
        const gDuration = g.durationMs || Math.round(duration / gamesList.length);

        if (!gameMap.has(gameName)) {
          gameMap.set(gameName, { durationMs: 0, sessions: 0 });
        }
        const gEntry = gameMap.get(gameName)!;
        gEntry.durationMs += gDuration;
        gEntry.sessions += 1;

        // Track per-room game count
        rEntry.games.set(gameName, (rEntry.games.get(gameName) || 0) + 1);
      });
    } else {
      // Default to timer name or Unknown if no agent game detected
      const fallbackName = s.timerName || 'Session standard';
      if (!gameMap.has(fallbackName)) {
        gameMap.set(fallbackName, { durationMs: 0, sessions: 0 });
      }
      const gEntry = gameMap.get(fallbackName)!;
      gEntry.durationMs += duration;
      gEntry.sessions += 1;
    }
  });

  // Calculate top games sorted by total duration
  const topGames: GameStat[] = Array.from(gameMap.entries())
    .map(([game, data]) => ({
      game,
      totalDurationMs: data.durationMs,
      sessionCount: data.sessions,
      percentage: totalDurationMs > 0 ? Math.round((data.durationMs / totalDurationMs) * 100) : 0,
    }))
    .sort((a, b) => b.totalDurationMs - a.totalDurationMs);

  // Calculate room stats sorted by duration
  const roomStats: RoomStat[] = Array.from(roomMap.entries())
    .map(([rId, data]) => {
      let topGame: string | undefined;
      let topGameCount = 0;
      data.games.forEach((count, gName) => {
        if (count > topGameCount) {
          topGameCount = count;
          topGame = gName;
        }
      });

      return {
        roomId: rId,
        roomName: data.name,
        totalDurationMs: data.durationMs,
        sessionCount: data.sessions,
        percentage: totalDurationMs > 0 ? Math.round((data.durationMs / totalDurationMs) * 100) : 0,
        topGame,
      };
    })
    .sort((a, b) => b.totalDurationMs - a.totalDurationMs);

  const averageSessionDurationMs =
    totalSessions > 0 ? Math.round(totalDurationMs / totalSessions) : 0;

  return {
    periodLabel,
    totalSessions,
    totalDurationMs,
    averageSessionDurationMs,
    uniqueRoomsCount: roomMap.size,
    uniqueStaffCount: staffSet.size,
    stopwatchCount,
    countdownCount,
    topGames,
    roomStats,
  };
}

/**
 * Génère une synthèse textuelle formatée en Markdown, prête à être partagée sur Discord, Slack ou par email.
 */
export function generateMarkdownReport(summary: EventReportSummary): string {
  const lines: string[] = [];

  lines.push(`# 🎮 Bilan d'Événement — namm (Frame Perfect)`);
  lines.push(`> Période : **${summary.periodLabel}** | Généré le : **${new Date().toLocaleDateString('fr-FR')}**\n`);

  lines.push(`## 📊 Chiffres Clés`);
  lines.push(`- **Temps de jeu total cumulé** : ${formatDuration(summary.totalDurationMs)}`);
  lines.push(`- **Sessions enregistrées** : ${summary.totalSessions}`);
  lines.push(`- **Durée moyenne par session** : ${formatDuration(summary.averageSessionDurationMs)}`);
  lines.push(`- **Postes / Salles actives** : ${summary.uniqueRoomsCount}`);
  lines.push(`- **Opérateurs / Staff impliqués** : ${summary.uniqueStaffCount}`);
  lines.push(`- **Répartition** : ${summary.countdownCount} Comptes à rebours / ${summary.stopwatchCount} Chronos libres\n`);

  if (summary.topGames.length > 0) {
    lines.push(`## 🏆 Top des Jeux`);
    summary.topGames.slice(0, 10).forEach((g, idx) => {
      lines.push(
        `${idx + 1}. **${g.game}** — ${formatDuration(g.totalDurationMs)} (${g.sessionCount} sessions, ${g.percentage}%)`
      );
    });
    lines.push('');
  }

  if (summary.roomStats.length > 0) {
    lines.push(`## 🖥️ Activité par Poste / Salle`);
    summary.roomStats.forEach((r) => {
      const topInfo = r.topGame ? ` (Top: ${r.topGame})` : '';
      lines.push(
        `- **${r.roomName}** : ${formatDuration(r.totalDurationMs)} across ${r.sessionCount} sessions${topInfo}`
      );
    });
    lines.push('');
  }

  lines.push(`---\n*Rapport généré automatiquement par namm Session Timer (Frame Perfect)*`);

  return lines.join('\n');
}

